import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { listUccV2 } from "../../../api/bseApi";
import UccUpdateModal from "./UccUpdateModal";
import Swal from "sweetalert2";
import {
  fetchInvestorVerificationLink,
  formatVerificationLinkError,
  openVerificationLinkSuccess,
} from "../../../utils/bseKycLink";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const accentColor = "#06d1c6";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "PENDING_AUTHENTICATION", label: "Pending authentication" },
  { value: "PENDING_AUTH", label: "Pending auth (short)" },
  { value: "PENDING_VERIFICATION", label: "Pending verification" },
  { value: "UCC CREATED", label: "UCC created" },
  { value: "REJECTED", label: "Rejected" },
  { value: "INACTIVE", label: "Inactive" },
];

const PAGE_SIZE = 50;

function statusBadgeStyle(status) {
  const s = String(status || "").toUpperCase().replace(/\s+/g, "_");
  if (s === "ACTIVE") return { bg: "#dcfce7", color: "#166534", border: "#86efac" };
  if (s.includes("PENDING_AUTH")) return { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" };
  if (s.includes("PENDING_VERIFICATION")) return { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" };
  if (s === "REJECTED") return { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" };
  if (s === "INACTIVE") return { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" };
  if (s.includes("CREATED")) return { bg: "#f3e8ff", color: "#6b21a8", border: "#d8b4fe" };
  return { bg: "#f8fafc", color: "#334155", border: "#e2e8f0" };
}

function mapUccRows(apiBody) {
  const data = apiBody?.data;
  const lists =
    (Array.isArray(data?.lists) && data.lists) ||
    (Array.isArray(data?.list) && data.list) ||
    (Array.isArray(data) && data) ||
    [];

  return lists.map((item, idx) => {
    const investor = item?.investor && typeof item.investor === "object" ? item.investor : {};
    const holders = Array.isArray(item?.holder) ? item.holder : item?.holder ? [item.holder] : [];
    const holder = holders[0] || {};
    const person = holder?.person && typeof holder.person === "object" ? holder.person : {};
    const identifiers = Array.isArray(holder?.identifier) ? holder.identifier : [];
    const panRow = identifiers.find(
      (id) => String(id?.identifier_type || "").toLowerCase() === "pan"
    );
    const name = [person.first_name, person.middle_name, person.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    const clientCode =
      investor.client_code ||
      item.client_code ||
      item.investor_client_code ||
      "";

    return {
      id: `${clientCode || "row"}-${idx}`,
      clientCode: clientCode || "—",
      uccStatus: item.ucc_status || item.status || "—",
      name: name || item.name || item.client_name || "—",
      pan: panRow?.identifier_number || item.pan || "—",
      holdingNature: item.holding_nature || "—",
      taxCode: item.tax_code || "—",
      accountType: item.is_client_demat
        ? "Demat"
        : item.is_client_physical
          ? "Physical"
          : "—",
      memberId: item?.member?.member_id || "",
    };
  });
}

export default function UccList() {
  const defaultMemberId = (process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim();

  const [memberId, setMemberId] = useState(defaultMemberId);
  const [statusFilter, setStatusFilter] = useState("");
  const [clientCodeSearch, setClientCodeSearch] = useState("");
  const [localSearch, setLocalSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    memberId: defaultMemberId,
    statusFilter: "",
    clientCodeSearch: "",
  }));

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [kycLinkLoadingCode, setKycLinkLoadingCode] = useState("");

  const fetchKycLink = async (clientCode, memberId) => {
    const code = String(clientCode || "").trim();
    if (!code || code === "—") return;
    setKycLinkLoadingCode(code);
    try {
      const mid =
        String(memberId || appliedFilters.memberId || defaultMemberId).trim() || "63164";
      const result = await fetchInvestorVerificationLink(code, mid);
      const ui = openVerificationLinkSuccess(result);
      if (ui.portalUrl && result.memberApproval) {
        const choice = await Swal.fire({
          icon: "info",
          title: ui.title,
          html: ui.html,
          showCancelButton: true,
          confirmButtonText: "Open BSE portal",
          cancelButtonText: "Close",
        });
        if (choice.isConfirmed) {
          window.open(ui.portalUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        Swal.fire({ icon: ui.isInfo ? "info" : "success", title: ui.title, html: ui.html });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Verification link failed",
        html: formatVerificationLinkError(err),
      });
    } finally {
      setKycLinkLoadingCode("");
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const mid = String(appliedFilters.memberId || defaultMemberId).trim() || "63164";
      const payload = {
        data: {
          member: { member_id: mid },
          fields: ["ALL"],
          count_only: false,
          start: (currentPage - 1) * PAGE_SIZE,
          length: PAGE_SIZE,
          investor: appliedFilters.clientCodeSearch.trim()
            ? { client_code: appliedFilters.clientCodeSearch.trim() }
            : {},
          filter_param: {},
          ...(appliedFilters.statusFilter
            ? { ucc_status: appliedFilters.statusFilter }
            : {}),
        },
      };

      const res = await listUccV2(payload);
      const body = res?.data || {};
      const st = String(body?.status ?? "").toLowerCase();
      const msgs = Array.isArray(body?.messages) ? body.messages : [];

      if (st === "error" || msgs.length > 0) {
        const first = msgs[0] || {};
        throw new Error(
          [first.errcode, first.field, Array.isArray(first.vals) ? first.vals.join(", ") : ""]
            .filter(Boolean)
            .join(" — ") || "BSE returned an error for list_ucc"
        );
      }

      const mapped = mapUccRows(body);
      setRows(mapped);
      const count = Number(body?.data?.count);
      setTotalCount(Number.isFinite(count) && count >= 0 ? count : mapped.length);
    } catch (err) {
      const api = err?.response?.data;
      setError(
        api?.message ||
          api?.data?.hint ||
          err?.message ||
          "Failed to load UCC list from BSE"
      );
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, defaultMemberId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filteredRows = useMemo(() => {
    const q = localSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.clientCode.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.pan.toLowerCase().includes(q) ||
        String(r.uccStatus).toLowerCase().includes(q)
    );
  }, [rows, localSearch]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters({
      memberId: memberId.trim() || defaultMemberId,
      statusFilter,
      clientCodeSearch,
    });
    setCurrentPage(1);
  };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div className="mb-4" style={cardStyle}>
            <div className="content-page-header content-page-headersplit d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">UCC list (BSE)</h5>
              <NavLink to="/admin/invest-bse/create-ucc" className="btn btn-sm text-white" style={{ background: accentColor }}>
                <i className="fa-solid fa-user-plus me-1" /> Create UCC
              </NavLink>
            </div>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/dashboard">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <NavLink to="/admin/invest-bse/profile-list">Invest Online BSE</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  UCC list
                </li>
              </ol>
            </nav>
            <p className="text-muted small mb-0">
              Live investor registrations from BSE Star MF <code>list_ucc</code>. Use status filter to match BSE portal tabs.
            </p>
          </div>

          <form onSubmit={handleApplyFilters} style={cardStyle}>
            <div className="row g-3 align-items-end">
              <div className="col-md-2">
                <label className="form-label small fw-semibold">Member ID</label>
                <input
                  className="form-control form-control-sm"
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value.replace(/\D/g, "").slice(0, 12))}
                  inputMode="numeric"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">UCC status (BSE)</label>
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold">Client code (server filter)</label>
                <input
                  className="form-control form-control-sm"
                  value={clientCodeSearch}
                  onChange={(e) => setClientCodeSearch(e.target.value)}
                  placeholder="Optional exact client code"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold">Quick search (this page)</label>
                <input
                  className="form-control form-control-sm"
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  placeholder="Name, PAN, code…"
                />
              </div>
              <div className="col-md-2 d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm flex-grow-1"
                  style={{ background: accentColor, color: "#fff", border: "none" }}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Load"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => fetchList()}
                  disabled={loading}
                  title="Refresh"
                >
                  <i className="fa fa-refresh" />
                </button>
              </div>
            </div>
          </form>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div style={cardStyle}>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <span className="text-muted small">
                {loading
                  ? "Loading from BSE…"
                  : `Showing ${filteredRows.length} row(s)${totalCount ? ` · total ${totalCount}` : ""}`}
              </span>
              <span className="text-muted small">
                Page {currentPage} of {totalPages}
              </span>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="table table-bordered table-hover mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>#</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Client code</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Status</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Name</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>PAN</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Holding</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Tax</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Type</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No UCC records found for the selected filters.
                      </td>
                    </tr>
                  )}
                  {filteredRows.map((row, index) => {
                    const badge = statusBadgeStyle(row.uccStatus);
                    return (
                      <tr key={row.id}>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          {(currentPage - 1) * PAGE_SIZE + index + 1}
                        </td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle", fontWeight: 600 }}>
                          {row.clientCode}
                        </td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: "999px",
                              fontSize: "12px",
                              fontWeight: 600,
                              background: badge.bg,
                              color: badge.color,
                              border: `1px solid ${badge.border}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.uccStatus}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{row.name}</td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{row.pan}</td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{row.holdingNature}</td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{row.taxCode}</td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{row.accountType}</td>
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          {row.clientCode && row.clientCode !== "—" ? (
                            <div className="d-flex flex-wrap gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                style={{ background: "#338599", color: "#fff" }}
                                onClick={() =>
                                  setEditRow({
                                    clientCode: row.clientCode,
                                    memberId:
                                      row.memberId ||
                                      String(appliedFilters.memberId || defaultMemberId).trim() ||
                                      "63164",
                                  })
                                }
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={kycLinkLoadingCode === row.clientCode}
                                onClick={() =>
                                  fetchKycLink(
                                    row.clientCode,
                                    row.memberId ||
                                      appliedFilters.memberId ||
                                      defaultMemberId
                                  )
                                }
                              >
                                {kycLinkLoadingCode === row.clientCode ? "…" : "KYC link"}
                              </button>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage <= 1 || loading}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="small text-muted">
                  Page <strong>{currentPage}</strong> of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage >= totalPages || loading}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>

        <UccUpdateModal
          open={Boolean(editRow)}
          clientCode={editRow?.clientCode || ""}
          memberId={editRow?.memberId || appliedFilters.memberId || defaultMemberId}
          onClose={() => setEditRow(null)}
          onUpdated={() => fetchList()}
        />
      </div>
    </div>
  );
}
